import React, { useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useModal } from '../useModal'
import { match } from 'ts-pattern'
import { HTMLOptions } from '../ModalHost'

type Props = {
  isOpen: boolean
  index?: number
  modalRoute?: boolean
  onClose?: () => void
  layoutId?: string
  children: React.ReactNode
  htmlOptions?: HTMLOptions
  key: string
  useQueryParams?: boolean
}

const Modal = (props: Props): JSX.Element => {
  const { isOpen, children, key, layoutId, htmlOptions, modalRoute, onClose } = props
  const { closeModal } = useModal()

  const getMaxWidth = useMemo(() => {
    return match(htmlOptions?.maxWidth)
      .with(600, () => 'max-w-[600px]')
      .with(800, () => 'max-w-[800px]')
      .with(1024, () => 'max-w-[1024px]')
      .with(1440, () => 'max-w-[1440px]')
      .otherwise(() => 'max-w-[1440px]')
  }, [htmlOptions?.maxWidth])

  useEffect(() => {
    if (modalRoute) {
      document.documentElement.style.overflow = 'hidden'

      return () => {
        document.documentElement.style.overflow = 'unset'
      }
    }
  }, [modalRoute])

  return (
    <div>
      {isOpen && (
        <motion.div
          key={key}
          layoutId={layoutId}
          className={'h-screen w-full sm:w-2/3 fixed top-0 right-0 z-[1000]'}>
          <motion.div
            key={`bg-${layoutId}`}
            className={
              'h-screen w-full fixed top-0 right-0 z-20 overscroll-contain bg-black/20'
            }
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            transition={{ type: 'spring', stiffness: 170, damping: 26, mass: 1 }}
            onClick={() => {
              closeModal()
              if (onClose) onClose()
            }}
          />

          <motion.div
            key={`modal-${layoutId}`}
            className={`flex h-screen absolute top-0 right-0 bg-white z-50 overscroll-contain w-full ${getMaxWidth}`}
            initial={{ opacity: 1, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 1, x: '100%', transition: { duration: 0.3 } }}
            transition={{
              type: 'spring',
              stiffness: 170,
              damping: 26,
              mass: 1,
            }}>
            {children}
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}

export default Modal
